import { useState, useCallback, useRef } from 'react';
import { processCardPayment, PaymentResult } from '../services/paymentService';

export const usePayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PaymentResult | null>(null);
  const paymentTimeout = useRef<NodeJS.Timeout>();

  const handleCardPayment = useCallback(async (amount: number) => {
    try {
      // Reset states
      setIsProcessing(true);
      setError(null);
      setResult(null);

      // Clear any existing timeout
      if (paymentTimeout.current) {
        clearTimeout(paymentTimeout.current);
      }

      // Process payment
      const paymentResult = await processCardPayment(amount);
      
      // Update states based on result
      setResult(paymentResult);
      if (!paymentResult.success) {
        setError(paymentResult.message);
      }

      return paymentResult;
    } catch (err) {
      // Handle errors
      const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue';
      
      setError(errorMessage);
      setResult({
        success: false,
        message: errorMessage
      });
      
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setIsProcessing(false);
      
      // Clear timeout
      if (paymentTimeout.current) {
        clearTimeout(paymentTimeout.current);
      }
    }
  }, []);

  const cancelPayment = useCallback(() => {
    setIsProcessing(false);
    setError(null);
    setResult(null);
    
    if (paymentTimeout.current) {
      clearTimeout(paymentTimeout.current);
    }
  }, []);

  return {
    isProcessing,
    error,
    result,
    handleCardPayment,
    cancelPayment
  };
};